import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  RefreshRounded,
  Search as SearchIcon,
  VisibilityRounded,
  InsertDriveFileRounded,
} from "@mui/icons-material";
import {
  type FormEvent,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  createTenant,
  deleteTenant,
  getTenants,
  updateTenant,
} from "../api/tenants";
import {
  getAllDocuments,
  getDocumentUrl,
  type AdminBenefitDocument,
} from "../api/tenant-documents";
import { useNotifications } from "../components/NotificationProvider";
import { formatDate } from "@/utils/formatDate";
import { normalizeDateInputValue } from "@/utils/date";

type TenantRecord = {
  id: number;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  date_of_birth?: string | null;
  national_insurance_number?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  notes?: string | null;
  archived?: boolean | null;
  created_at?: string;
};

type TenantFormState = {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  phone: string;
  email: string;
  national_insurance_number: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  notes: string;
  archived: boolean;
};

const createInitialFormState = (): TenantFormState => ({
  first_name: "",
  last_name: "",
  date_of_birth: "",
  phone: "",
  email: "",
  national_insurance_number: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  notes: "",
  archived: false,
});

export function TenantsPage({ token }: { token: string }) {
  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedTenant, setSelectedTenant] = useState<TenantRecord | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<TenantRecord | null>(
    null,
  );
  const [formState, setFormState] = useState<TenantFormState>(
    createInitialFormState,
  );
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsTenant, setDetailsTenant] = useState<TenantRecord | null>(null);
  const [detailsDocuments, setDetailsDocuments] = useState<
    AdminBenefitDocument[]
  >([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const { showAlert, showSnackbar } = useNotifications();

  const loadTenants = async () => {
    setIsLoading(true);
    try {
      const data = await getTenants();
      setTenants(data);
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Unable to load tenants",
        message: "Please try again shortly.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openDetailsModal = async (tenant: TenantRecord) => {
    setDetailsTenant(tenant);
    setDetailsModalOpen(true);
    setDetailsLoading(true);
    try {
      const docs = await getAllDocuments(token, undefined, tenant.id);
      setDetailsDocuments(docs);
    } catch {
      setDetailsDocuments([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    void loadTenants();
  }, []);

  const filteredTenants = useMemo(() => {
    const term = deferredSearchTerm.trim().toLowerCase();
    if (!term) return tenants;

    return tenants.filter((tenant) => {
      const haystack = [
        tenant.first_name,
        tenant.last_name,
        tenant.email ?? "",
        tenant.phone ?? "",
        tenant.notes ?? "",
        tenant.national_insurance_number ?? "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [deferredSearchTerm, tenants]);

  const openCreateModal = () => {
    setModalMode("create");
    setSelectedTenant(null);
    setFormState(createInitialFormState());
    setIsModalOpen(true);
  };

  const openEditModal = (tenant: TenantRecord) => {
    setModalMode("edit");
    setSelectedTenant(tenant);
    setFormState({
      first_name: tenant.first_name,
      last_name: tenant.last_name,
      date_of_birth: normalizeDateInputValue(tenant.date_of_birth),
      phone: tenant.phone ?? "",
      email: tenant.email ?? "",
      national_insurance_number: tenant.national_insurance_number ?? "",
      emergency_contact_name: tenant.emergency_contact_name ?? "",
      emergency_contact_phone: tenant.emergency_contact_phone ?? "",
      notes: tenant.notes ?? "",
      archived: tenant.archived ?? false,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTenant(null);
    setFormState(createInitialFormState());
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formState.first_name.trim() || !formState.last_name.trim()) {
      showAlert({
        title: "Missing details",
        message: "First name and last name are required.",
        variant: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        first_name: formState.first_name.trim(),
        last_name: formState.last_name.trim(),
        date_of_birth: formState.date_of_birth || null,
        phone: formState.phone.trim() || null,
        email: formState.email.trim() || null,
        national_insurance_number:
          formState.national_insurance_number.trim() || null,
        emergency_contact_name: formState.emergency_contact_name.trim() || null,
        emergency_contact_phone:
          formState.emergency_contact_phone.trim() || null,
        notes: formState.notes.trim() || null,
        archived: formState.archived,
      };

      if (modalMode === "edit" && selectedTenant) {
        await updateTenant(selectedTenant.id, payload);
        showSnackbar({
          title: "Tenant updated",
          message: "The tenant record was updated successfully.",
          variant: "success",
        });
      } else {
        await createTenant(payload);
        showSnackbar({
          title: "Tenant added",
          message: "The tenant record is now available.",
          variant: "success",
        });
      }

      closeModal();
      await loadTenants();
    } catch (error) {
      console.error(error);
      showAlert({
        title:
          modalMode === "edit"
            ? "Unable to update tenant"
            : "Unable to add tenant",
        message: "Please confirm the details and try again.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteConfirmation = (tenant: TenantRecord) => {
    setTenantToDelete(tenant);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!tenantToDelete) {
      return;
    }

    setDeleteDialogOpen(false);
    setIsLoading(true);
    try {
      await deleteTenant(tenantToDelete.id);
      await loadTenants();
      showSnackbar({
        title: "Tenant deleted",
        message: "The tenant record was removed.",
        variant: "info",
      });
    } catch (error) {
      console.error(error);
      const message =
        error instanceof Error
          ? error.message
          : "This tenant could not be removed.";
      showAlert({
        title: "Unable to delete tenant",
        message,
        variant: "error",
      });
    } finally {
      setIsLoading(false);
      setTenantToDelete(null);
    }
  };

  return (
    <Stack spacing={3} sx={{ width: "100%", position: "relative" }}>
      {isLoading && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: -8,
            left: 0,
            right: 0,
            borderRadius: 6,
          }}
        />
      )}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 6,
          p: { xs: 2, md: 3 },
          bgcolor: "background.paper",
          boxShadow: "0 10px 35px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by name, email, phone, or notes..."
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              maxHeight: 40,
              "& .MuiOutlinedInput-root": { borderRadius: 6 },
            }}
          />
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh tenants">
              <IconButton
                onClick={() => void loadTenants()}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 6,
                }}
              >
                <RefreshRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateModal}
              disableElevation
              sx={{ borderRadius: 6, px: 3, whiteSpace: "nowrap", height: 40 }}
            >
              Add tenant
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 6, p: { xs: 2, md: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Tenants ({filteredTenants.length})
        </Typography>

        <Stack spacing={2}>
          {filteredTenants.map((tenant) => (
            <Card
              key={tenant.id}
              variant="outlined"
              sx={{
                borderRadius: 6,
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  boxShadow: 2,
                  borderColor: "primary.main",
                },
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                  flexWrap: "wrap",
                  "&:last-child": { pb: 2 },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 260 }}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "primary.main",
                        width: 42,
                        height: 42,
                        fontWeight: 700,
                      }}
                    >
                      {tenant.first_name?.charAt(0) ?? "T"}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {tenant.first_name} {tenant.last_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.2 }}
                      >
                        {tenant.email || tenant.phone
                          ? `${tenant.email ?? ""}${tenant.email && tenant.phone ? " • " : ""}${tenant.phone ?? ""}`
                          : "No contact details"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 1.25, flexWrap: "wrap" }}
                  >
                    <Chip
                      label={tenant.archived ? "Archived" : "Active"}
                      size="small"
                      color={tenant.archived ? "default" : "primary"}
                      variant={tenant.archived ? "outlined" : "filled"}
                      sx={{ borderRadius: 6, fontWeight: 600 }}
                    />
                    {tenant.national_insurance_number ? (
                      <Chip
                        label={`NI: ${tenant.national_insurance_number}`}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: 6, fontWeight: 600 }}
                      />
                    ) : null}
                  </Stack>
                </Box>

                <Stack
                  direction="row"
                  spacing={1}
                  alignSelf={{ xs: "flex-end", sm: "center" }}
                >
                  <Tooltip title="View details">
                    <IconButton
                      color="primary"
                      onClick={() => openDetailsModal(tenant)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 6,
                      }}
                    >
                      <VisibilityRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit tenant">
                    <IconButton
                      color="primary"
                      onClick={() => openEditModal(tenant)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 6,
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete tenant">
                    <IconButton
                      color="error"
                      onClick={() => openDeleteConfirmation(tenant)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 6,
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <Box sx={{ minWidth: 260 }}>
                  <Typography variant="body2" color="text.secondary">
                    DOB: {formatDate(tenant.date_of_birth) ?? "Not provided"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Emergency: {tenant.emergency_contact_name ?? "Not provided"}
                    {tenant.emergency_contact_phone
                      ? ` • ${tenant.emergency_contact_phone}`
                      : ""}
                  </Typography>
                  {tenant.notes ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.75 }}
                    >
                      {tenant.notes}
                    </Typography>
                  ) : null}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {filteredTenants.length === 0 && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              No tenants match your current search.
            </Typography>
          </Box>
        )}
      </Paper>

      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        aria-labelledby="add-tenant-dialog-title"
        PaperProps={{ sx: { borderRadius: 6, width: "100%", maxWidth: 720 } }}
      >
        <DialogTitle id="add-tenant-dialog-title">
          {modalMode === "edit" ? "Edit tenant" : "Add tenant"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Capture the tenant’s core identity, contact, and emergency details.
          </DialogContentText>
          <Box
            component="form"
            id="tenant-form"
            onSubmit={handleSubmit}
            noValidate
          >
            <Stack spacing={2.25}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  value={formState.first_name}
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      first_name: event.target.value,
                    }))
                  }
                  label="First name"
                  required
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
                />
                <TextField
                  value={formState.last_name}
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      last_name: event.target.value,
                    }))
                  }
                  label="Last name"
                  required
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  value={formState.date_of_birth}
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      date_of_birth: event.target.value,
                    }))
                  }
                  label="Date of birth"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
                />
                <TextField
                  value={formState.phone}
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      phone: event.target.value,
                    }))
                  }
                  label="Phone"
                  type="tel"
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  value={formState.email}
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      email: event.target.value,
                    }))
                  }
                  label="Email"
                  type="email"
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
                />
                <TextField
                  value={formState.national_insurance_number}
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      national_insurance_number: event.target.value,
                    }))
                  }
                  label="National Insurance number"
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  value={formState.emergency_contact_name}
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      emergency_contact_name: event.target.value,
                    }))
                  }
                  label="Emergency contact name"
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
                />
                <TextField
                  value={formState.emergency_contact_phone}
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      emergency_contact_phone: event.target.value,
                    }))
                  }
                  label="Emergency contact phone"
                  type="tel"
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
                />
              </Stack>

              <TextField
                value={formState.notes}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    notes: event.target.value,
                  }))
                }
                label="Notes"
                multiline
                minRows={3}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formState.archived}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        archived: event.target.checked,
                      }))
                    }
                  />
                }
                label="Archived"
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeModal} color="inherit" sx={{ borderRadius: 6 }}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="tenant-form"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 6 }}
          >
            {modalMode === "edit" ? "Update tenant" : "Save tenant"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-tenant-dialog-title"
        PaperProps={{ sx: { borderRadius: 6 } }}
      >
        <DialogTitle id="delete-tenant-dialog-title">Delete tenant</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>
              {tenantToDelete?.first_name} {tenantToDelete?.last_name}
            </strong>
            ? All of their tenancies and rent payment records will also be
            deleted. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="inherit"
            sx={{ borderRadius: 6 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 6 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tenant Details Modal */}
      <Dialog
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setDetailsTenant(null);
          setDetailsDocuments([]);
        }}
        PaperProps={{ sx: { borderRadius: 6, width: "100%", maxWidth: 720 } }}
      >
        <DialogTitle>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                bgcolor: "primary.main",
                width: 42,
                height: 42,
                fontWeight: 700,
              }}
            >
              {detailsTenant?.first_name?.charAt(0) ?? "T"}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {detailsTenant?.first_name} {detailsTenant?.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {detailsTenant?.email || detailsTenant?.phone
                  ? `${detailsTenant?.email ?? ""}${detailsTenant?.email && detailsTenant?.phone ? " • " : ""}${detailsTenant?.phone ?? ""}`
                  : "No contact details"}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading && <LinearProgress sx={{ borderRadius: 6, mb: 2 }} />}

          <Stack spacing={3}>
            {/* Personal Details */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "primary.main",
                }}
              >
                Personal Details
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Date of Birth
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDate(detailsTenant?.date_of_birth) ?? "Not provided"}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    NI Number
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {detailsTenant?.national_insurance_number ?? "Not provided"}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Status */}
            <Box>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={detailsTenant?.archived ? "Archived" : "Active"}
                  color={detailsTenant?.archived ? "default" : "primary"}
                  variant={detailsTenant?.archived ? "outlined" : "filled"}
                  sx={{ borderRadius: 6, fontWeight: 600 }}
                />
              </Stack>
            </Box>

            {/* Emergency Contact */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "primary.main",
                }}
              >
                Emergency Contact
              </Typography>
              <Typography variant="body2">
                {detailsTenant?.emergency_contact_name ?? "Not provided"}
                {detailsTenant?.emergency_contact_phone
                  ? ` • ${detailsTenant.emergency_contact_phone}`
                  : ""}
              </Typography>
            </Box>

            {/* Notes */}
            {detailsTenant?.notes && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "primary.main",
                  }}
                >
                  Notes
                </Typography>
                <Typography variant="body2">{detailsTenant.notes}</Typography>
              </Box>
            )}

            {/* Uploaded Documents */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "primary.main",
                }}
              >
                Uploaded Documents ({detailsDocuments.length})
              </Typography>
              {detailsDocuments.length > 0 ? (
                <Stack spacing={1}>
                  {detailsDocuments.map((doc) => (
                    <Card
                      key={doc.id}
                      variant="outlined"
                      sx={{ borderRadius: 3 }}
                    >
                      <CardContent
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          py: 1.25,
                          "&:last-child": { pb: 1.25 },
                        }}
                      >
                        <InsertDriveFileRounded
                          color="primary"
                          fontSize="small"
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {doc.file_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(doc.uploaded_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Chip
                          label={doc.status}
                          size="small"
                          color={
                            doc.status === "approved"
                              ? "success"
                              : doc.status === "rejected"
                                ? "error"
                                : "warning"
                          }
                          sx={{
                            borderRadius: 6,
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        />
                        <Tooltip title="View file">
                          <IconButton
                            size="small"
                            onClick={async () => {
                              try {
                                const response = await fetch(
                                  getDocumentUrl(doc.id),
                                  {
                                    headers: {
                                      Authorization: `Bearer ${token}`,
                                    },
                                  },
                                );
                                if (!response.ok) throw new Error("Failed");
                                const blob = await response.blob();
                                const url = URL.createObjectURL(blob);
                                window.open(
                                  url,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              } catch {
                                showAlert({
                                  title: "Cannot open file",
                                  message: "Failed to load.",
                                  variant: "error",
                                });
                              }
                            }}
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 6,
                            }}
                          >
                            <VisibilityRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No documents uploaded.
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDetailsModalOpen(false);
              setDetailsTenant(null);
              setDetailsDocuments([]);
            }}
            color="inherit"
            sx={{ borderRadius: 6 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
